import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/v3-work.css';
import { MarqueeStrip, Folder, OSWindow } from './motifs';

function WorkExperience() {
  const [openId, setOpenId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const canvasRef = useRef(null);

  const roles = [
    {
      id: 'job0',
      title: 'Junior Unity Developer',
      company: 'DJ XLabs, Delhi NCR, India',
      dateRange: 'Nov 2025 -> NOW',
      current: true,
      tileImage: '/assets/images/UsingVR.jpg',
      monogram: 'DJ',
      label: 'DJXLABS',
      position: { top: '4%', left: '14%', rotate: -3 },
      summary: 'Production Unity and C# on XR, Android and Windows',
      bullets: [
        'Multiplayer MR with Unity Netcode: colocation, shared-space state, authority and ownership.',
        'Custom Unity editor tools and internal packages that cut repeat setup work across projects.',
        'GPU compute shaders, URP shader work, and profiling against a headset frame budget.',
        'Local SQLite persistence feeding REST upload pipelines with batching and retry.',
        'Event-driven architecture: ScriptableObject and static event buses, SOLID refactors for multi-developer codebases.',
        'Hardware and sensor integration: pressure-sensitive stylus, depth camera, WebSocket bridges.',
        'Multilingual text pipelines, including Arabic RTL and Devanagari cluster handling.'
      ]
    },
    {
      id: 'job00',
      title: 'Unity Developer Intern',
      company: 'DJ XLabs, Delhi NCR, India',
      dateRange: 'Sep 2025 -> Nov 2025',
      current: false,
      tileImage: '/assets/images/UsingVR.jpg',
      monogram: 'DJ',
      label: 'DJXLABS',
      position: { top: '29%', left: '58%', rotate: 4 },
      bullets: [
        'Built custom Unity editor tooling to automate repetitive build and content workflows, and implemented multi-language support with RTL text rendering via RTL-TMPro. Worked on a production VR game.'
      ]
    },
    {
      id: 'job1',
      title: 'VR Developer Intern',
      company: 'CXR, GITAM',
      dateRange: '2024 → 2025',
      current: false,
      tileImage: '/assets/images/XRBootcampTeamPic.jpg',
      monogram: 'CX',
      label: 'CXR',
      position: { top: '52%', left: '16%', rotate: -2 },
      bullets: [
        'Developed and implemented a cutting-edge virtual reality aircraft simulation utilizing Unity 3D URP and XR Interaction Toolkit, resulting in attracting 3 new clients to the consultancy project.',
        'Created interactive door mechanisms, lever operations, and realistic lighting effects for Oculus Quest 2 using C# significantly improving user immersion and overall experience feedback.',
        'Worked on creating an interactive analyzer tool for a Ship project, enabling clients to visualize and analyze ship components in real-time.',
        'Utilized Notion for project management, engineering a tracking system that increased team productivity by 25%, prioritized tasks effectively, and facilitated clear communication of updates.'
      ]
    },
    {
      id: 'job2',
      title: 'Vice President Outreach',
      company: 'Entrepreneurs Club, GITAM',
      dateRange: '2024 → 2025',
      current: false,
      tileImage: '/assets/images/LeadingEclubAsVP.jpg',
      monogram: 'EC',
      label: 'E_CLUB',
      position: { top: '68%', left: '58%', rotate: 3 },
      bullets: [
        'Executed strategic planning for 4 diverse portfolios, orchestrating the successful execution of 35+ high-impact events that engaged and empowered over 2000 students, cultivating a dynamic entrepreneurial environment within the university campus.',
        'Coordinated and facilitated networking opportunities by organizing and moderating 20+ speaker sessions featuring top industry professionals, resulting in invaluable insights being shared with aspiring entrepreneurs.',
        'Initiated outreach campaigns targeting local schools to foster an entrepreneurial mindset among students through interactive workshops and events, ultimately inspiring over 200 young minds to pursue their entrepreneurial dreams.'
      ]
    }
  ];

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <section className="v3-work" id="work-experience" data-aos="fade-up" data-aos-duration="1000">
      <div className="v3-work-eyebrow">[ EXPERIENCE · 2024 → NOW ]</div>
      <h2 className="v3-work-title">Work.</h2>

      <div className="v3-work-marquee">
        <MarqueeStrip items={['ENGINEERING IMMERSIVE EXPERIENCES', '→', 'SHIPPING XR', '→', 'BUILDING TOOLS', '→']} />
      </div>

      <div className="v3-work-stage">
        <div className="v3-folder-desktop" ref={canvasRef}>
          <div className="v3-folder-desktop__timeline">
            <span>[ 2026 ]</span>
            <span>[ 2025 ]</span>
            <span>[ 2024 ]</span>
            <span>[ 2024 ]</span>
          </div>

          {roles.map((role, index) => (
            <motion.div
              key={role.id}
              className="v3-folder-position"
              data-hover-label="Click to know more"
              style={{ top: role.position.top, left: role.position.left }}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.4, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
              drag
              dragConstraints={canvasRef}
              dragElastic={0.18}
              dragMomentum={false}
              whileTap={{ scale: 0.98 }}
              onTap={() => setOpenId(openId === role.id ? null : role.id)}
            >
              <Folder
                label={role.label}
                rotation={openId === role.id ? 0 : role.position.rotate}
                active={openId === role.id}
                current={role.current}
                color={role.current ? 'var(--sun)' : 'var(--cobalt)'}
                image={role.tileImage}
              />
              <div className="v3-folder-caption">
                <div className="v3-folder-caption__title">{role.title}</div>
                <div className="v3-folder-caption__date">{role.dateRange}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="v3-work-info">
          <AnimatePresence mode="wait">
            {openId ? (
              (() => {
                const role = roles.find(r => r.id === openId);
                return (
                  <motion.div
                    key={role.id}
                    className="v3-folder-reveal"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <OSWindow filename={`${role.id}.role`} onClose={() => setOpenId(null)}>
                      <div className="v3-folder-reveal__photo">
                        <img src={role.tileImage} alt="" />
                      </div>
                      <div className="v3-folder-reveal__meta">
                        <span className="v3-folder-reveal__date">{role.dateRange}</span>
                        <span className="v3-folder-reveal__company">{role.company}</span>
                        {role.current && <span className="v3-folder-reveal__tag">[ CURRENT ]</span>}
                      </div>
                      <h3 className="v3-folder-reveal__title">{role.title}</h3>
                      {role.summary && <p className="v3-folder-reveal__summary">{role.summary}</p>}
                      <ul className="v3-folder-reveal__bullets">
                        {role.bullets.map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    </OSWindow>
                  </motion.div>
                );
              })()
            ) : (
              <motion.div
                key="hint"
                className="v3-work-info__hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                [ TAP A FOLDER TO OPEN ]
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

export default WorkExperience;
